import { API_TYPES, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

const workBadgeEmailRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workBadgeEmail),
    attributes: z.object({
      qualification: z.string(),
    }),
  }),
});

const workBadgeEmailResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workBadgeEmail),
    id: z.string().min(1),
    attributes: z.object({
      workerId: z.string().min(1),
      qualification: z.string().min(1),
      status: z.string().min(1),
    }),
  }),
});

interface SendWorkBadgeEmailParams {
  workerId: string;
  qualification: string;
}

export type SendWorkBadgeEmailResponse = z.infer<typeof workBadgeEmailResponseSchema>;
export type SendWorkBadgeEmailRequest = z.infer<typeof workBadgeEmailRequestSchema>;

export function useSendWorkBadgeEmail(
  options: UseMutationOptions<SendWorkBadgeEmailResponse, Error, SendWorkBadgeEmailParams> = {}
) {
  return useMutation({
    mutationFn: async ({ workerId, qualification }) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/work-badge-emails`,
        data: {
          data: {
            type: "work-badge-email",
            attributes: {
              qualification,
            },
          },
        },
        requestSchema: workBadgeEmailRequestSchema,
        responseSchema: workBadgeEmailResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.WORK_BADGE_EMAIL_SEND_FAILURE,
    },
    ...options,
  });
}
